import { Component, OnInit, Input, OnChanges, SimpleChanges, NgZone, OnDestroy } from '@angular/core';
import { GoogleChartService } from '../services/google-chart.service';
import { Router, ActivatedRoute, RouterEvent, NavigationEnd } from '@angular/router';
import { pseghrlyLoadsTbl } from '../Models/LoadsData';
import { DatePipe } from '@angular/common';
import { MinMaxDate } from '../Models/MiscModels';
import { PsegHrlyDemandService } from '../services/pseg-hrly-demand.service';
import { SignalrISOdataService } from '../services/signalr-ISOdata.service';
import { MiscService } from '../services/misc.service';
import { NONE_TYPE } from '@angular/compiler/src/output/output_ast';
import { ResizedEvent } from 'angular-resize-event';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-pseg-hrly-load-chart',
  templateUrl: './pseg-hrly-load-chart.component.html',
  styleUrls: ['./pseg-hrly-load-chart.component.css']
})
export class PsegHrlyLoadChartComponent implements OnInit, OnDestroy {
  public destroyed = new Subject<any>();
  private gLib: any;
  private minMaxDate: MinMaxDate;

  private psegHrlyLoadDashboard: any;

  private psegHrlyLoadLine: any;
  private psegHrlyLoadDateSlider: any;
  public btnLabel = 'Show as Table';
  private psegHrlyLoadTable;
  private chartTitle: string;

  constructor(private gChartService: GoogleChartService, private route: ActivatedRoute,
    private signalrService: SignalrISOdataService, private _ngZone: NgZone, private miscSvc: MiscService) {

  }

  private getOptions(chartType) {
    let options;

    switch (chartType) {

      case 'ComboChart':
        options = {
          enableInteractivity: true,
          interpolateNulls: 'false',
          series: {

            0: {
              type: 'bars', color: '#c8393c',
              targetAxisIndex: 1, lineWidth: 2, interpolateNulls: 'false', opacity: 0.01
            },
            1: { type: 'bars', color: 'black', targetAxisIndex: 0 }
            , 2: { type: 'line', targetAxisIndex: 1, color: '#13f9f6', lineWidth: 6, interpolateNulls: 'false' }
            , 3: { type: 'steppedArea', targetAxisIndex: 0, color: '#c0c0c0', lineWidth: 2, interpolateNulls: 'false', opacity: 0.01 }
            , 4: { type: 'steppedArea', targetAxisIndex: 0, color: 'orange', lineWidth: 3, interpolateNulls: 'false', opacity: 0.04 }
            , 5: { type: 'steppedArea', targetAxisIndex: 0, color: 'red', lineWidth: 7, interpolateNulls: 'false', opacity: 0.05 }
          },

          colors: ['#13f9f6'],
          legend: {
            position: 'top', alignment: 'end', textStyle: { fontSize: 14 },
          },
          titleTextStyle: {
            color: 'black',    // any HTML string color ('red', '#cc00cc')
            fontName: 'Montesserat', // i.e. 'Times New Roman'
            fontSize: '22', // 12, 18 whatever you want (don't specify px)
            bold: false,    // true or false
            italic: true,   // true of false
          },
          chartArea: { width: '80%', height: '80%' },
          crosshair: { trigger: 'both' },
          curveType: 'none',

          vAxes: [
            {
              title: 'Consumption Load', textStyle: { fontSize: '12' },
              format: '#,### KW',
              minValue: 0, maxValue: 50, gridlines: { count: 0 }
            },
            {
              title: 'PSEG Zone Load', textStyle: { fontSize: '12' },
              format: '#,### MW'
            } // , minValue: 2000, maxValue: 10000, gridlines: { count: 8 }
          ],
          hAxis: {
            textStyle: {
              fontSize: '12'
            },
            gridlines: {
              count: -1,
              units: {
                days: { format: ['MMM dd'] },
                hours: { format: ['HH:mm', 'ha'] },
              }
            },
            minorGridlines: {
              units: {
                hours: { format: ['hh:mm:ss a', 'ha'] },
                minutes: { format: ['HH:mm a Z', ':mm'] }
              }
            },

          },
        };

        break;

      case 'Table':
        options = {
          showRowNumber: true,
          width: '80%',
          height: '80%'
        };
        break;

      default:
        options = {};
    }

    return options;
  }

  getFilterOptions() {
    const controlOptions = {
      // Filter by the date axis.
      'filterColumnIndex': 0,
      ui: {
        'chartOptions': {
          colors: ['grey'],
          chartArea: { width: '80%', height: '100%' },
          hAxis: {
            textStyle: {
              fontSize: '14'
            },
            gridlines: {
              count: -1,
              units: {
                days: { format: ['MMM dd'] },
                hours: { format: ['HH:mm', 'ha'] },
              }
            },
            minorGridlines: {
              units: {
                hours: { format: ['hh:mm:ss a', 'ha'] },
                minutes: { format: ['HH:mm a Z', ':mm'] }
              }
            }
          }
        },
      }
    };
    return controlOptions;
  }

  ngOnInit() {

    this.gLib = this.gChartService.getGoogle();
    this.gLib.charts.load('current', { 'packages': ['corechart', 'table', 'controls'] });
    this.gLib.charts.setOnLoadCallback(this.drawPsegLoadChart.bind(this));
    this.psegHrlyLoadTable = new this.gLib.visualization.DataTable();

    this.signalrService.PsegHrlyLoadsRefreshMessageReceived
    .pipe(takeUntil(this.destroyed))
    .subscribe((data) => {
      this._ngZone.run(() => {
        this.updateChartDataTable(data);

      });
    });

  }
  formatAxes() {
    const date_formatter = new this.gLib.visualization.DateFormat({ pattern: 'MMM dd, yyyy,  h:mm aa' });
    date_formatter.format(this.psegHrlyLoadTable, 0);  // Where 0 is the index of the column

    const y1formatter = new this.gLib.visualization.NumberFormat({ suffix: ' KW', pattern: '#,###' });
    y1formatter.format(this.psegHrlyLoadTable, 1); // Apply formatter to second column

    const y2formatter = new this.gLib.visualization.NumberFormat({ suffix: ' MW', pattern: '#,###' });
    y2formatter.format(this.psegHrlyLoadTable, 2); // Apply formatter to second column

    // y2formatter.format(this.psegHrlyLoadTable, 3); // Apply formatter to second column
  }

  private createChartDataTable(data: Array<pseghrlyLoadsTbl>) {

    this.psegHrlyLoadTable.addColumn('date', 'Date');
    this.psegHrlyLoadTable.addColumn('number', 'PSE&G Hourly Load');
    this.psegHrlyLoadTable.addColumn('number', 'Consumption Load');

    this.psegHrlyLoadTable.addColumn('number', 'PSE&G Hourly Forecast');

    this.psegHrlyLoadTable.addColumn({ type: 'string', role: 'style' });

    this.psegHrlyLoadTable.addColumn('number', 'Inferred Peaks');
    this.psegHrlyLoadTable.addColumn({ type: 'string', role: 'tooltip' });

    this.psegHrlyLoadTable.addColumn('number', 'Monthly Candidate Peaks');
    this.psegHrlyLoadTable.addColumn({ type: 'string', role: 'tooltip' });

    this.psegHrlyLoadTable.addColumn('number', 'Extended Peaks');
    this.psegHrlyLoadTable.addColumn({ type: 'string', role: 'tooltip' });

    const date = new Date(data[0].timestamp);
    const evalTimeStr = date.toLocaleString() + ' (EST)';
    data.splice(0, 1);

    this.chartTitle = 'PSE&G Zone Load at hourly intervals. Last Reading at ' + evalTimeStr;

    this.addRowsChartDataTable(data);
  }

  private addRowsChartDataTable(data: Array<pseghrlyLoadsTbl>) {
    const chartRow = new Array<Date | number | string | boolean>();

    for (let i = 0; i < data.length; i++) {

      chartRow.length = 0;
      const date = new Date(data[i].timestamp);

      chartRow.push(date);

      chartRow.push(data[i].hrlyInstLoad
        / (data[i].psNumReads));

      chartRow.push(data[i].hrlyWatts
        / (data[i].meterNumReads * 1000));

      chartRow.push(data[i].hrlyForecstLoad
        / (data[i].forecstNumReads));

      chartRow.push('opacity:.5');

      const peak = data[i].peak;
      if ((peak !== 0 || peak != null) && (peak === 1)) {
        chartRow.push(50);
        chartRow.push('Local Peak:  Hour ending at ' + date.toString());
      } else {
        chartRow.push(0);
        chartRow.push('Local Peak End:  Hour starting at ' + date.toString());
      }

      if ((peak !== 0 || peak != null) && (peak === 2)) {
        chartRow.push(50);
        chartRow.push('Current Monthly Peak:  Hour ending at ' + date.toString());
      } else {
        chartRow.push(0);
        chartRow.push('Current Monthly Peak End:  Hour starting at ' + date.toString());
      }

      if ((peak !== 0 || peak != null) && (peak === 3)) {
        chartRow.push(50);
        chartRow.push('Extended Peak:  Hour ending at ' + date.toString());
      } else {
        chartRow.push(0);
        chartRow.push(' ');
      }

      this.psegHrlyLoadTable.addRow(chartRow);


    }
    this.formatAxes();
  }

  private updateChartDataTable(data: Array<pseghrlyLoadsTbl>) {

    if (this.psegHrlyLoadDashboard !== undefined) {
      const maxEvalAtStr = this.miscSvc.removeRowsHrlyLoad(this.psegHrlyLoadTable, data);
      this.addRowsChartDataTable(data);
      this.updatePsegLoadChart(data, maxEvalAtStr);
    }  }

  private createLoadChartWrapper() {   // Line chart visualization

    this.psegHrlyLoadLine = new this.gLib.visualization.ChartWrapper({
      'chartType': 'ComboChart',
      'containerId': 'psegHrlyLoadline_div',
    });
    const options = this.getOptions('ComboChart');
    this.psegHrlyLoadLine.setOptions(options);
    this.psegHrlyLoadLine.setOption('title', this.chartTitle);
  }
  private drawPsegLoadChart(options) {

    this.createChartDataTable(this.route.snapshot.data.PsegHrlyLoadData);


    // Create a dashboard.
    const dash_container = document.getElementById('psegHrlyLoaddashboard_div');


    this.psegHrlyLoadDashboard = new this.gLib.visualization.Dashboard(dash_container);

    const dtNumRows = this.psegHrlyLoadTable.getNumberOfRows();

    const MaxDt = new Date(this.psegHrlyLoadTable.getValue(dtNumRows - 1, 0));


    const MinDt = new Date(MaxDt);
    MinDt.setDate(MaxDt.getDate() - 10);



    // Create a date range slider
    this.psegHrlyLoadDateSlider = new this.gLib.visualization.ControlWrapper({
      'controlType': 'ChartRangeFilter',
      'containerId': 'psegHrlyLoadcontrol_div',

    });
    const state_obj = { 'range': { 'start': MinDt, 'end': MaxDt } };
    this.psegHrlyLoadDateSlider.setState(state_obj);
    const controlOptions = this.getFilterOptions();
    this.psegHrlyLoadDateSlider.setOptions(controlOptions);


    this.createLoadChartWrapper();


    // Bind psegHrlyLoadLine to the dashboard, and to the controls
    // this will make sure our line chart is updated when our date changes
    this.psegHrlyLoadDashboard.bind(this.psegHrlyLoadDateSlider, this.psegHrlyLoadLine);

    const chartType = this.psegHrlyLoadLine.getChartType();
    if (chartType === 'Table') {
      const view = new this.gLib.visualization.DataView(this.psegHrlyLoadTable);
      // view.hideColumns([4, 5]);
      this.psegHrlyLoadDashboard.draw(view, { sortColumn: 0 });
    } else {
      this.psegHrlyLoadDashboard.draw(this.psegHrlyLoadTable);
    }
  }

  private updatePsegLoadChart(data: Array<pseghrlyLoadsTbl>, MaxEvalAtStr) {



    if (this.psegHrlyLoadDashboard !== undefined) {
      this.chartTitle = 'PSE&G Zone Load at hourly intervals. Last Reading at ' + MaxEvalAtStr;
      this.psegHrlyLoadLine.setOption('title', this.chartTitle);

      let state_obj = this.psegHrlyLoadDateSlider.getState();
      const startDt = state_obj.range.start;
      const endDt: Date = new Date(data[data.length - 1].timestamp);
      state_obj = { 'range': { 'start': startDt, 'end': endDt } };

      const chartType = this.psegHrlyLoadLine.getChartType();
      if (chartType === 'Table') {
        const view = new this.gLib.visualization.DataView(this.psegHrlyLoadTable);
        view.hideColumns([4, 5]);
        this.psegHrlyLoadDashboard.draw(view, { sortColumn: 0 });
      } else {
        this.psegHrlyLoadDashboard.draw(this.psegHrlyLoadTable);
      }

    }
  }

  onResized(event: ResizedEvent) {
    const width = event.newWidth;
    const height = event.newHeight;

    if (this.psegHrlyLoadLine !== undefined) {
      this.psegHrlyLoadLine.draw();
      this.psegHrlyLoadDateSlider.draw();
    }
  }
  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
  private switchChart() {

    let chartType;
    const view = new this.gLib.visualization.DataView(this.psegHrlyLoadTable);

    // view.hideColumns([4, 5]);
    // tslint:disable-next-line:triple-equals
    if (this.btnLabel == 'Show as Chart') {
      chartType = 'ComboChart';
      this.psegHrlyLoadLine.setChartType(chartType);
      this.btnLabel = 'Show as Table';
    } else {
      chartType = 'Table';
      this.psegHrlyLoadLine.setChartType(chartType);
      this.btnLabel = 'Show as Chart';

    }
    const ChartType = this.psegHrlyLoadLine.getChartType();

    const options = this.getOptions(chartType);
    this.psegHrlyLoadLine.setOptions(options);
    this.psegHrlyLoadLine.setOption('title', this.chartTitle);
    if (chartType === 'Table') {
      this.psegHrlyLoadDashboard.draw(view, { sortColumn: 0 });
    } else {
      this.psegHrlyLoadDashboard.draw(this.psegHrlyLoadTable);
    }
  }

}

